import React, {HTMLAttributeAnchorTarget} from 'react';
import cn from 'classnames';
import CircularProgress from '../CircularProgress';
import {ComponentSize, StandardProps} from '..';
import TouchRipple from '../TouchRipple';

type ButtonColorType =
  | 'inherit'
  | 'primary'
  | 'secondary'
  | 'tertiary'
  | 'brandTertiary'
  | 'success'
  | 'error'
  | 'errorSecondary'
  | 'errorTertiary'
  | 'gray'
  | 'grayTertiary'
  | 'transparent'
  | 'white'
  ;

type ButtonVariant =
  | 'contained'
  | 'outlined'
  | 'text'
  ;

function getButtonColorClassName(color?: ButtonColorType) {
  switch (color) {
    case 'inherit':
      return 'button--inherit';
    case 'primary':
      return 'button--primary';
    case 'secondary':
      return 'button--secondary';
    case 'tertiary':
      return 'button--tertiary';
    case 'brandTertiary':
      return 'button--brandTertiary';
    case 'success':
      return 'button--success';
    case 'error':
      return 'button--error';
    case 'errorSecondary':
      return 'button--errorSecondary';
    case 'errorTertiary':
      return 'button--errorTertiary';
    case 'gray':
      return 'button--gray';
    case 'grayTertiary':
      return 'button--grayTertiary';
    case 'transparent':
      return 'button--transparent';
    case 'white':
      return 'button--white';
    default:
      return '';
  }
}

function getButtonVariantClassName(variant?: ButtonVariant) {
  switch (variant) {
    case 'contained':
      return 'button--contained';
    case 'outlined':
      return 'button--outlined';
    case 'text':
      return 'button--text';
    default:
      return '';
  }
}

function getButtonSizeClassName(size?: ComponentSize) {
  switch (size) {
    case 'extra-small':
      return 'button--extra-small';
    case 'small':
      return 'button--small';
    case 'medium':
      return 'button--medium';
    case 'large':
      return 'button--large';
    default:
      return '';
  }
}

interface ButtonRootProps extends StandardProps<React.HTMLAttributes<HTMLButtonElement | HTMLAnchorElement>> {
  color?: ButtonColorType;
  variant?: ButtonVariant;
  size?: ComponentSize;
  disabled?: boolean;
  fullWidth?: boolean;
  component?: string | React.ElementType;
  target?: HTMLAttributeAnchorTarget | undefined;
  href?: string;
  to?: string;
  type?: 'button' | 'reset' | 'submit';
  autoFocus?: boolean;
  startIcon?: React.ReactNode;
  value?: any;
}

export const ButtonRoot = React.forwardRef(
  (
    {
      className,
      color = 'primary',
      variant,
      size = 'medium',
      type = 'button',
      component = 'button',
      autoFocus,
      startIcon: startIconProp,
      ...props
    }: ButtonRootProps,
    ref: React.Ref<HTMLButtonElement | HTMLAnchorElement>,
  ) => {
    const innerRef = React.useRef<HTMLButtonElement | HTMLAnchorElement>(null);

    React.useImperativeHandle(ref, () => innerRef.current as HTMLButtonElement | HTMLAnchorElement);

    const startIcon = startIconProp && <span className={'button__start-icon'}>{startIconProp}</span>;

    React.useEffect(() => {
      const btnEl = innerRef.current;

      if (autoFocus && btnEl) {
        btnEl.focus();
      }
    }, [autoFocus]);

    let Component = component;

    if (Component === 'button' && (props.href || props.to)) {
      Component = 'a';
    }

    return (
      <Component
        ref={innerRef}
        className={cn(
          'button button__root',
          getButtonVariantClassName(variant),
          getButtonSizeClassName(size),
          getButtonColorClassName(color),
          {
            'button--disabled': !!props.disabled,
          },
          className,
        )}
        type={type}
        {...props}
      >
        {startIcon}
        {props.children}
      </Component>
    );
  },
);

ButtonRoot.displayName = 'ButtonRoot';

export interface ButtonProps extends ButtonRootProps {
  loading?: boolean;
  disableRipple?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps>(
  ({disabled, loading, disableRipple = false, ...buttonProps}, ref) => {
    const innerRef = React.useRef<HTMLButtonElement | HTMLAnchorElement>(null);

    React.useImperativeHandle(ref, () => innerRef.current as HTMLButtonElement | HTMLAnchorElement);

    return (
      <ButtonRoot ref={innerRef} disabled={disabled || loading} {...buttonProps}>
        {buttonProps.children}
        {loading && (
          <CircularProgress
            size={24}
            style={{
              color: 'var(--brand-primary)',
              position: 'absolute',
              top: '50%',
              left: '50%',
              marginTop: '-12px',
              marginLeft: '-12px',
              zIndex: 2,
            }}
          />
        )}
        {disableRipple ? null : <TouchRipple targetRef={innerRef} />}
      </ButtonRoot>
    );
  },
);

Button.displayName = 'Button';

export default Button;
